'use client';

import { useEffect } from 'react';
import Lenis from 'lenis';

// Tracks per-route opt-outs. The watch page (and any page that owns its
// own scroll container / video gestures) sets `[data-lenis-disable]` on
// the document root to bypass the smooth wrapper.
function shouldDisableLenis(): boolean {
  if (typeof document === 'undefined') return false;
  return Object.hasOwn(document.documentElement.dataset, 'lenisDisable');
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
  );
}

// Radix portals/overlays + any opt-out marker. Inside any of these
// nodes Lenis must hand the wheel back to the browser so the popover /
// dialog / sheet / scroll-area's own scroller receives the event.
//
// Kept as one combined selector so the prevent() hot path is a single
// `closest()` call rather than a sequence.
const PORTAL_SELECTOR = [
  '[role="dialog"]',
  '[role="alertdialog"]',
  '[role="menu"]',
  '[role="listbox"]',
  // Radix dropdowns / popovers / hover-cards / tooltips / selects all
  // wrap their floating element in this Popper wrapper.
  '[data-radix-popper-content-wrapper]',
  // shadcn's ScrollArea sets this on the inner viewport.
  '[data-radix-scroll-area-viewport]',
  // Vaul (mobile sheets) — uses `[vaul-drawer]` on the content root.
  '[vaul-drawer]',
  // Manual opt-out: any element can declare `data-lenis-prevent` to
  // keep Lenis off its wheel/scroll handling.
  '[data-lenis-prevent]',
].join(',');

// Walk ancestors looking for any vertically scrollable element. If we
// find one with actual overflow content, the browser should handle the
// wheel event — otherwise Lenis would scroll the document while the
// user's pointer is over an inner scroller, which feels broken.
function hasScrollableAncestor(node: HTMLElement): boolean {
  let el: HTMLElement | null = node;
  while (el && el !== document.documentElement && el !== document.body) {
    const style = getComputedStyle(el);
    const overflowY = style.overflowY;
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      el.scrollHeight > el.clientHeight
    ) {
      return true;
    }
    el = el.parentElement;
  }
  return false;
}

function createLenisInstance(): Lenis {
  return new Lenis({
    // lerp controls per-frame interpolation intensity. 0.12 settles
    // in ~18 frames (~300ms @ 60fps), which feels snappy.
    lerp: 0.12,
    // Used by programmatic scrollTo() calls only. Anchor jumps and
    // scrollIntoView still get the gentle settle.
    duration: 0.8,
    easing: (t) => 1 - 2 ** (-10 * t),
    smoothWheel: true,
    // Don't intercept touch — mobile browsers handle scroll natively
    // and overriding breaks pull-to-refresh on iOS / Android.
    syncTouch: false,
    // Lenis manages its own rAF, idle-aware. We used to drive the
    // loop manually which kept rAF running 60Hz even when no scroll
    // animation was in flight.
    autoRaf: true,
    prevent: (node) => {
      // Fast path: known overlay containers + manual opt-outs.
      if (node.closest(PORTAL_SELECTOR)) return true;
      // Slow path: any ancestor with real vertical overflow. Catches
      // bespoke inner scrollers (sidebars, settings panes, modals
      // that don't expose a `role="dialog"` for whatever reason).
      return hasScrollableAncestor(node);
    },
  });
}

export function LenisProvider(): null {
  useEffect(() => {
    if (prefersReducedMotion()) {
      return () => {};
    }

    // Heavy pages (watch player, /discover) set `data-lenis-disable`
    // on <html> to fully bypass Lenis — programmatic scrollTo at 60Hz
    // forces a main-thread layout every frame and tanks fps on pages
    // with sticky sidebars + animated transforms. A MutationObserver
    // re-checks the attribute so per-route useEffects can toggle Lenis
    // back on when the user leaves the heavy page.
    let lenis: Lenis | null = null;

    const enable = () => {
      if (lenis) return;
      lenis = createLenisInstance();
    };

    const disable = () => {
      if (!lenis) return;
      lenis.destroy();
      lenis = null;
    };

    if (!shouldDisableLenis()) enable();

    const mo = new MutationObserver(() => {
      if (shouldDisableLenis()) disable();
      else enable();
    });
    mo.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-lenis-disable'],
    });

    // Pause Lenis when the tab is hidden — no point burning rAF on a
    // hidden page, and the browser would deprioritize it anyway.
    const onVisibilityChange = () => {
      if (!lenis) return;
      if (document.hidden) lenis.stop();
      else lenis.start();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      mo.disconnect();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      disable();
    };
  }, []);

  return null;
}

export default LenisProvider;
